import { Fragment, useState } from 'react'
import { PickList, SlideOver, TextField } from '@bloo-team/ui'
import type { WorkbookDoc } from '@bloo-team/api-client'
import { findFolder, findFolderByPath, useFolders } from '../../lib/detection-list'
import { optionsWith, useWorkbookMeta } from '../../lib/workbook-blocks'
import { addParameter, patchParameter, removeParameter } from '../../lib/workbook-draft'
import type { WorkbookDraft } from '../../lib/workbook-draft'
import {
  MONTHS, WEEK_DAYS, cronLooksValid, scheduleEquals, scheduleOf, scheduleProblems, scheduleValid, scheduleWire,
  timeLooksValid, toggleIn,
} from '../../lib/workbook-schedule'
import type { CronKind, RunSchedule, ScheduleMode } from '../../lib/workbook-schedule'
import { FolderTree } from '../detections'

/**
 * The Settings tab of the workbook page (#205, design B · Tab), three
 * sections:
 *
 * - **Placement** (folder, stage, profile) and **Parameters** are edited in
 *   Edit mode and join the page's draft — one Save… writes them with the
 *   blocks. In View they read only (the draft's values while one is open).
 * - **Run mode** is live in View and Edit alike: Off / Streamed / Scheduled,
 *   always UTC, saved at once by **Apply** as its own version (the page owns
 *   the save; this section only says what to apply).
 *
 * Props in, events out; the stage / profile lists and the folder tree are
 * read here through their lib hooks.
 */

export type DraftSettings = Pick<WorkbookDraft, 'path' | 'stage' | 'profile' | 'parameters'>

export interface RunModeControl {
  /** Apply is allowed (not locked, Security Engineer, latest version). */
  enabled: boolean
  /** Why not, when not. */
  reason: string | null
  pending: boolean
  error: string | null
  onApply: (next: RunSchedule) => void
}

export interface SettingsTabProps {
  clusterId: string
  scopeId: string
  /** The document shown — the latest, or the old version being viewed. */
  doc: WorkbookDoc
  /** Placement + parameters as shown: the draft's while one is open. */
  settings: DraftSettings
  editing: boolean
  onSettings: (patch: Partial<DraftSettings>) => void
  runMode: RunModeControl
}

/* --------------------------------------------------------------- placement */

function Placement({
  clusterId, scopeId, settings, editing, onSettings,
}: Pick<SettingsTabProps, 'clusterId' | 'scopeId' | 'settings' | 'editing' | 'onSettings'>) {
  const [picking, setPicking] = useState(false)
  const stageQ = useWorkbookMeta({ clusterId, type: 'stage' })
  const profileQ = useWorkbookMeta({ clusterId, type: 'profile' })
  const foldersQ = useFolders({ clusterId, scopeId })
  const tree = foldersQ.data ?? []

  return (
    <section className="hud-panel wb-pad wb-set" aria-label="Placement">
      <span className="hud-eyebrow">placement</span>
      {editing ? (
        <div className="wb-grid-3">
          <div className="bui-field">
            <span className="bui-label">folder</span>
            <span className="hud-row wb-gap-2">
              <span className="mono wb-set-path" aria-label="Folder">{settings.path || '/'}</span>
              <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={() => setPicking(true)}>
                Choose…
              </button>
            </span>
          </div>
          <div className="bui-field">
            <span className="bui-label">stage</span>
            <PickList
              label="Stage"
              placeholder="stage…"
              options={optionsWith(stageQ.data ?? [], settings.stage)}
              selected={settings.stage ? [settings.stage] : []}
              onChange={(next) => onSettings({ stage: next[0] ?? settings.stage })}
            />
          </div>
          <div className="bui-field">
            <span className="bui-label">profile</span>
            <PickList
              label="Profile"
              placeholder="profile…"
              options={optionsWith(profileQ.data ?? [], settings.profile)}
              selected={settings.profile ? [settings.profile] : []}
              onChange={(next) => onSettings({ profile: next[0] ?? settings.profile })}
            />
          </div>
        </div>
      ) : (
        <dl className="wb-kv">
          <dt>folder</dt>
          <dd className="mono">{settings.path || '/'}</dd>
          <dt>stage</dt>
          <dd>{settings.stage || '—'}</dd>
          <dt>profile</dt>
          <dd>{settings.profile || '—'}</dd>
        </dl>
      )}

      <SlideOver open={picking} onClose={() => setPicking(false)} eyebrow="placement" title="Choose a folder">
        <FolderTree
          folders={tree}
          selectedId={findFolderByPath(tree, settings.path)?.id ?? null}
          isPending={foldersQ.isPending}
          isError={foldersQ.isError}
          onSelect={(id) => {
            const folder = id ? findFolder(tree, id) : null
            if (!folder) return // "All" is not a folder
            onSettings({ path: folder.path })
            setPicking(false)
          }}
        />
      </SlideOver>
    </section>
  )
}

/* ---------------------------------------------------------------- run mode */

const MODES: Array<{ mode: ScheduleMode; label: string }> = [
  { mode: 'off', label: 'Off' },
  { mode: 'streamed', label: 'Streamed' },
  { mode: 'scheduled', label: 'Scheduled' },
]
const KINDS: Array<{ kind: CronKind; label: string }> = [
  { kind: 'daily', label: 'Daily' },
  { kind: 'weekly', label: 'Weekly' },
  { kind: 'monthly', label: 'Monthly' },
  { kind: 'custom', label: 'Custom' },
]

function Toggles<T extends string>({
  label, items, pressed, disabled, onPress,
}: {
  label: string
  items: ReadonlyArray<{ id: T; label: string }>
  pressed: (id: T) => boolean
  disabled: boolean
  onPress: (id: T) => void
}) {
  return (
    <div className="bui-toggles wb-wrap" role="group" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          className="bui-toggle"
          aria-pressed={pressed(it.id)}
          disabled={disabled}
          onClick={() => onPress(it.id)}
        >
          {it.label}
        </button>
      ))}
    </div>
  )
}

function RunMode({ doc, control }: { doc: WorkbookDoc; control: RunModeControl }) {
  const saved = scheduleOf(doc)
  const [s, setS] = useState<RunSchedule>(saved)
  const set = (patch: Partial<RunSchedule>) => setS((cur) => ({ ...cur, ...patch }))
  const off = !control.enabled || control.pending
  const unchanged = scheduleEquals(saved, s)
  const valid = scheduleValid(s)
  const missing = scheduleProblems(s)
  const wire = scheduleWire(s)
  const timeField = (
    <TextField
      label="time"
      isRequired
      hint="HH:mm · UTC"
      placeholder="HH:mm"
      value={s.time}
      isDisabled={off}
      error={s.time && !timeLooksValid(s.time) ? 'use HH:mm, 00:00 – 23:59' : undefined}
      onChange={(v) => set({ time: v.trim() })}
    />
  )

  return (
    <section className="hud-panel wb-pad wb-set" aria-label="Run mode">
      <span className="hud-row wb-gap-2">
        <span className="hud-eyebrow">run mode</span>
        <span className="bui-chip" title="Schedules always run in UTC">UTC</span>
      </span>
      <Toggles
        label="Run mode"
        items={MODES.map((m) => ({ id: m.mode, label: m.label }))}
        pressed={(m) => s.mode === m}
        disabled={off}
        onPress={(mode) => set({ mode })}
      />
      {s.mode === 'scheduled' ? (
        <div className="hud-stack wb-gap-2">
          <Toggles
            label="Schedule"
            items={KINDS.map((k) => ({ id: k.kind, label: k.label }))}
            pressed={(k) => s.kind === k}
            disabled={off}
            onPress={(kind) => set({ kind })}
          />
          {s.kind === 'custom' ? (
            <TextField
              label="cron"
              isRequired
              hint="five fields: minute hour day-of-month month day-of-week · UTC"
              placeholder="*/20 * * * *"
              value={s.cron}
              isDisabled={off}
              error={s.cron.trim() && !cronLooksValid(s.cron) ? 'a cron has five fields' : undefined}
              onChange={(v) => set({ cron: v })}
            />
          ) : (
            <div className="wb-grid-3">
              {timeField}
              {s.kind === 'monthly' ? (
                <TextField
                  label="day of month"
                  isRequired
                  hint="1 – 31"
                  inputMode="numeric"
                  value={s.dayOfMonth ? String(s.dayOfMonth) : ''}
                  isDisabled={off}
                  error={s.dayOfMonth < 1 || s.dayOfMonth > 31 ? 'a day from 1 to 31' : undefined}
                  onChange={(v) => {
                    const n = parseInt(v.replace(/\D/g, ''), 10)
                    set({ dayOfMonth: Number.isNaN(n) ? 0 : n })
                  }}
                />
              ) : null}
            </div>
          )}
          {s.kind === 'weekly' ? (
            <Toggles
              label="Days"
              items={WEEK_DAYS.map((d) => ({ id: d, label: d }))}
              pressed={(d) => s.days.includes(d)}
              disabled={off}
              onPress={(d) => set({ days: toggleIn(s.days, d, WEEK_DAYS) })}
            />
          ) : null}
          {s.kind === 'monthly' ? (
            <Toggles
              label="Months"
              items={MONTHS.map((m) => ({ id: m, label: m }))}
              pressed={(m) => s.months.includes(m)}
              disabled={off}
              onPress={(m) => set({ months: toggleIn(s.months, m, MONTHS) })}
            />
          ) : null}
        </div>
      ) : null}
      <span className="hud-row wb-gap-2 wb-wrap">
        <button
          type="button"
          className="bui-btn bui-btn-solid bui-btn-sm"
          disabled={off || unchanged || !valid}
          onClick={() => control.onApply(s)}
        >
          {control.pending ? 'Applying…' : 'Apply'}
        </button>
        {s.mode === 'scheduled' && valid ? <span className="hud-unit mono">cron {wire.cron}</span> : null}
        {!off && missing.length ? (
          <span className="hud-unit" role="status">Required: {missing.join(', ')}.</span>
        ) : null}
        {control.enabled ? (
          <span className="hud-unit">Apply saves a new version at once; an open draft is kept.</span>
        ) : control.reason ? (
          <span className="hud-unit">{control.reason}</span>
        ) : null}
      </span>
      {control.error ? (
        <div className="bui-alert bui-alert-error" role="alert">
          <span className="bui-alert-mark">✕</span>
          <span>{control.error}</span>
        </div>
      ) : null}
    </section>
  )
}

/* -------------------------------------------------------------- parameters */

function Parameters({ settings, editing, onSettings }: Pick<SettingsTabProps, 'settings' | 'editing' | 'onSettings'>) {
  const ps = settings.parameters
  const set = (parameters: DraftSettings['parameters']) => onSettings({ parameters })
  return (
    <section className="hud-panel wb-pad wb-set" aria-label="Parameters">
      <span className="hud-eyebrow">parameters</span>
      {editing ? (
        <div className="hud-stack wb-gap-1">
          {ps.length ? (
            <div className="wb-row wb-row-param" aria-hidden="true">
              <span className="bui-label">Name</span>
              <span className="bui-label">Default Value</span>
              <span />
            </div>
          ) : null}
          {ps.map((p, i) => (
            // Index keys: rows are positional, and names may be blank or repeat while typing.
            <div key={i} className="wb-row wb-row-param">
              <TextField
                aria-label={`Parameter ${i + 1} name`}
                placeholder="name"
                value={p.name}
                error={p.name.trim() ? undefined : 'a name is needed'}
                onChange={(v) => set(patchParameter(ps, i, { name: v }))}
              />
              <TextField
                aria-label={`Parameter ${i + 1} default value`}
                placeholder="default value"
                value={p.value}
                onChange={(v) => set(patchParameter(ps, i, { value: v }))}
              />
              <button type="button" className="row-action" aria-label={`Remove parameter ${i + 1}`} onClick={() => set(removeParameter(ps, i))}>
                ✕
              </button>
            </div>
          ))}
          <div>
            <button type="button" className="list-chip" onClick={() => set(addParameter(ps))}>
              + parameter
            </button>
          </div>
        </div>
      ) : ps.length === 0 ? (
        <p className="hud-unit wb-flush">This workbook has no parameters.</p>
      ) : (
        <dl className="wb-kv" aria-label="Parameter defaults">
          {ps.map((p, i) => (
            <Fragment key={i}>
              <dt>{p.name || '—'}</dt>
              <dd className="mono">{p.value || '—'}</dd>
            </Fragment>
          ))}
        </dl>
      )}
    </section>
  )
}

/* --------------------------------------------------------------------- tab */

export function SettingsTab({ clusterId, scopeId, doc, settings, editing, onSettings, runMode }: SettingsTabProps) {
  return (
    <div className="hud-stack wb-gap-hud">
      <Placement clusterId={clusterId} scopeId={scopeId} settings={settings} editing={editing} onSettings={onSettings} />
      {/* Keyed on the saved version: an Apply (or a version switch) reopens from the new doc. */}
      <RunMode key={`${doc.id}:${doc.version}`} doc={doc} control={runMode} />
      <Parameters settings={settings} editing={editing} onSettings={onSettings} />
    </div>
  )
}
